import { Button } from "@/components/ui/button";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { useChatStream } from "@/contexts/chat-stream-context";
import { useApi, useApiRequest } from "@/hooks/useApi";
import { useExitTransition } from "@/hooks/useExitTransition";
import { useKeyboardInset } from "@/hooks/useKeyboardInset";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useIsPeopleSearchOffered } from "@/hooks/useIsPeopleSearchOffered";
import { useTranslation } from "@/hooks/useTranslation";
import { readAutoTacticsSetting } from "@/hooks/useAutoTacticsSetting";
import { readClarificationSetting } from "@/hooks/useClarificationSetting";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { findProducedArtifacts } from "@/lib/artifact";
import { cn } from "@/lib/utils";
import {
  chatArtifactPanelStore,
  useChatArtifactPanel,
} from "@/stores/chat-artifact-panel-store";
import { useChat } from "@ai-sdk/react";
import { useUser } from "@clerk/react";
import {
  ChatForcedTool,
  ChatRequestMode,
  ClarificationSkip,
  CompanyDTO,
  ConversationDTO,
  ConversationFileDTO,
  ConversationMessageMetadata,
  ConversationMessagePromptInfo,
  CursorPaginatedResponse,
  LLMModels,
  LLMSelection,
  truncateToLastCleanBoundary,
} from "@shaarp/common";
import { ChatStatus, DefaultChatTransport, FileUIPart, UIMessage } from "ai";
import { ArrowDownIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useToastError } from "../ui/use-toast";
import {
  asksClarification,
  resolveRequestAim,
} from "./chat-clarification-run";
import { ChatArtifactPanel } from "./chat-artifact-panel";
import {
  ARTIFACT_PANEL_TRANSITION_MS,
  ChatArtifactSplit,
} from "./chat-artifact-split";
import { ChatConversationSkeleton } from "./chat-conversation-skeleton";
import { ChatComposerDisclaimer } from "./chat-dialog-primitives";
import { ChatMessages } from "./chat-messages";
import { ChatNew } from "./chat-new";
import { ChatContextUsageInfo } from "./chat-context-usage";
import { ChatTextInput } from "./chat-text-input";
import { FILES_UPLOAD_SIZE_LIMIT_LABEL } from "@/lib/conversation-files";
import {
  buildAnswerLostErrorMessage,
  buildChatErrorMessage,
} from "./chat-error-message";
import { isLastMessageUnanswered } from "./chat-unanswered-turn";
import {
  buildOptimisticUserMessage,
  resolveOptimisticStatus,
} from "./optimistic-message";

/**
 * How many times a dropped response is picked back up on its own. Past this the
 * thread waits for a signal that something changed — the tab coming back, or
 * the network — rather than retrying into a connection that is still down.
 */
const MAX_REATTACH_ATTEMPTS = 5;
const REATTACH_DELAY_MS = 800;

export type MemoryMessageToSend = {
  llmId: LLMSelection;
  text: string;
  filesAttached: File[];
  uploadedFiles?: ConversationFileDTO[];
  forcedTool?: ChatForcedTool | null;
  /** Request mode picked in the composer for this message. */
  requestMode?: ChatRequestMode | null;
  /** Set when the message is a reply walking past a clarifying question. */
  clarificationSkipped?: ClarificationSkip;
  companyContext?: CompanyDTO | null;
  prompt?: ConversationMessagePromptInfo;
};

/**
 * Flags the trailing assistant message as cut short, so the thread says so
 * without waiting for a reload.
 *
 * The type carries *who* cut it, because that decides whether a resume is
 * offered: `INTERRUPTED` is a failure the app owns (the wall-clock deadline
 * above all — the reason this exists) and gets a resume button;
 * `STOPPED_BY_USER` is a deliberate stop, reported but never offered for
 * resuming.
 *
 * Written through as given, never re-derived from what is in the bubble. The
 * API stores the same two types on the same rule, so any second opinion here
 * is a reload changing the card under the reader. How much of the answer
 * survived is a question for the notice, not for the type.
 */
const markLastMessageInterrupted = (
  messages: UIMessage<ConversationMessageMetadata>[],
  type: "INTERRUPTED" | "STOPPED_BY_USER"
): UIMessage<ConversationMessageMetadata>[] => {
  const lastIndex = messages.length - 1;
  const lastMessage = messages[lastIndex];
  if (lastMessage?.role !== "assistant" || !lastMessage.metadata) {
    return messages;
  }

  return messages.map((message, index) =>
    index === lastIndex && message.metadata
      ? { ...message, metadata: { ...message.metadata, type } }
      : message
  );
};

type SetChatMessages = (
  messages:
    | UIMessage<ConversationMessageMetadata>[]
    | ((
        messages: UIMessage<ConversationMessageMetadata>[]
      ) => UIMessage<ConversationMessageMetadata>[])
) => void;

/**
 * Errors raised before the turn was ever created: the message never left, so the
 * draft is handed back to the composer instead of being lost. The explanation is
 * rendered in the thread like any other assistant answer.
 *
 * `context_budget_exceeded` does not belong here even though it reads like a
 * refusal: it is raised inside the stream, after the user message has been
 * stored. Handing the draft back would have the user post the very same message
 * a second time, and the thread would show it twice — once without an answer.
 */
// Refused before the message is stored: a document the model cannot read, or
// a team locked out by the API's guard (`TEAM_BLOCKED`).
const SEND_REFUSED_ERROR_CODES = new Set(["document_too_large", "TEAM_BLOCKED"]);

/**
 * How often a streamed answer is allowed to re-render the thread, by the length
 * it has reached. Longest first, so the first match is the widest that applies.
 *
 * React counts commits that leave work still pending and throws "Maximum update
 * depth exceeded" once fifty of them follow each other — which kills the SSE
 * mid-answer and stores the reply as if the user had stopped it. Once a message
 * is long enough that rendering it takes about as long as the interval itself,
 * the next batch is always waiting when a commit ends, the count never resets
 * and the page dies after a few seconds of that. Measured over ten unattended
 * runs of a 6000-word answer: six of seven crashed at 50 ms, none of three at
 * 120 ms.
 *
 * A staircase rather than one wider interval, because the mechanism is a race
 * between the cost of a commit and the interval, and the cost grows with the
 * message: a single step only moves the length at which the two meet again.
 * The 250 ms step is not answering an observed crash — 120 ms held through a
 * 171 000-character run — it is the margin a slower machine needs, bought at
 * four refreshes a second on an answer already past sixty thousand characters.
 * Adding a step is one line here.
 *
 * The narrowest interval is the one short answers get, since they are the
 * common case and the only ones where the difference would be visible.
 */
const STREAM_THROTTLE_STEPS = [
  { chars: 60_000, ms: 250 },
  { chars: 12_000, ms: 120 },
] as const;
const STREAM_THROTTLE_MS = 50;

/**
 * The interval an answer of this length streams at.
 *
 * Changing it re-subscribes the SDK's message store (`throttleWaitMs` is a
 * dependency of its `subscribe`), which costs one unthrottled render per step
 * crossed — the snapshot behind it is a ref, so nothing else is invalidated.
 * Once per step, per answer.
 */
const throttleForAnswerLength = (chars: number): number =>
  STREAM_THROTTLE_STEPS.find((step) => chars > step.chars)?.ms ??
  STREAM_THROTTLE_MS;

/** Characters of text an assistant message has streamed so far. */
const answerTextLength = (
  message: UIMessage<ConversationMessageMetadata> | undefined
): number =>
  message?.role === "assistant"
    ? message.parts.reduce(
        (total, part) => total + (part.type === "text" ? part.text.length : 0),
        0
      )
    : 0;

/**
 * Maps an upload failure to the explanation that actually helps.
 *
 * `tooLarge` is on the user's side and has a concrete fix; `storage` is on
 * ours and only time fixes it; anything else is most likely the network.
 */
function resolveUploadFailureReason(
  error: unknown,
): "tooLarge" | "storage" | "unknown" {
  const response = (
    error as { response?: { status?: number; data?: { code?: string } } }
  )?.response;

  if (response?.status === 413) return "tooLarge";
  if (response?.data?.code === "file_storage_unavailable") return "storage";
  return "unknown";
}

export function ChatConversation({
  conversation,
  pendingMessage: pendingMessageProp,
  isNewConversation,
  onMessageGenerated = async () => {},
  onPendingMessageSent,
}: {
  conversation: ConversationDTO;
  pendingMessage?: MemoryMessageToSend | null;
  /**
   * Just created, so there is no history to read: the pending message shows
   * at once. Left out, a pending message at mount is taken to mean that — true
   * where a conversation is only ever handed a message as it is created. A
   * page that also hands one to an existing conversation says which it is.
   */
  isNewConversation?: boolean;
  onMessageGenerated?: (conversation: ConversationDTO) => void;
  onPendingMessageSent?: () => void;
}) {
  const { t } = useTranslation();
  const toastError = useToastError();
  const api = useApi();
  const { isMobile } = useDeviceFormat();
  // Whether this chat can show a people table beside the conversation, sent
  // with every request so the server offers the people tools only then
  // (#668). The composer reads the same hook to decide whether it promises
  // people. Read through a ref by the transport, which is built once.
  const isPeopleSearchOffered = useIsPeopleSearchOffered({
    isCompanyTargeted: !!conversation.companyTargeted,
  });
  const isPeopleSearchEnabledRef = useRef(false);
  isPeopleSearchEnabledRef.current = isPeopleSearchOffered;
  const { url, headers } = useApiRequest();
  const { user } = useUser();
  const { setIsStreamInProgress, registerStopFunction, stopStreamBeforeSend } =
    useChatStream();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { llmSelection } = useLlmSelection();

  // A `?m=<messageId>` param (set when opening a search result) asks the view to
  // scroll straight to that message. Captured once at mount so the initial
  // scroll-to-bottom / skeleton-settle can be skipped when we have a target.
  const targetMessageId = searchParams.get("m");
  const hasInitialScrollTargetRef = useRef(!!targetMessageId);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const handledTargetRef = useRef<string | null>(null);

  // Get pending message from prop (CompanyChat) or navigation state (ChatConversationPage)
  const pendingMessageFromState = (
    location.state as { pendingMessage?: MemoryMessageToSend } | null
  )?.pendingMessage;
  const pendingMessage = pendingMessageProp ?? pendingMessageFromState;

  // A conversation just created has no history to load: its pending message
  // renders optimistically and the fetch is skipped. An existing one reads
  // its history first, and the pending message is sent after it.
  const initialPendingRef = useRef<MemoryMessageToSend | null>(
    (isNewConversation ?? !!pendingMessage) ? (pendingMessage ?? null) : null
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastSubmittedMessageRef = useRef<MemoryMessageToSend | null>(null);
  // Set by the stop button so `onFinish` can tell a deliberate stop from an
  // abort the app caused; the SDK reports both as `isAbort`.
  const isUserStopRef = useRef(false);
  /** A send is under way, before `status` has had a chance to say so. */
  const isSendingRef = useRef(false);
  const setMessagesRef = useRef<SetChatMessages | null>(null);
  const [inlineErrorMessage, setInlineErrorMessage] =
    useState<UIMessage<ConversationMessageMetadata> | null>(null);
  // A send that failed before it reached the SDK: nothing the stream reports
  // will ever say so, see `resolveOptimisticStatus`.
  const [hasSendFailed, setHasSendFailed] = useState(false);

  const [isLoading, setIsLoading] = useState(!initialPendingRef.current);
  // While settling we keep the content hidden behind the skeleton and only
  // reveal it once it has scrolled to — and stabilized at — the very bottom, so
  // the user never sees the intermediate scroll while late blocks grow in. Skip
  // this for freshly created conversations, which should appear immediately.
  const [isSettling, setIsSettling] = useState(
    !initialPendingRef.current && !hasInitialScrollTargetRef.current
  );
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isCompacting, setIsCompacting] = useState(false);
  // Read by the compaction handler so it can guard against a double run without
  // taking `isCompacting` as a dependency — its identity has to stay stable for
  // the memoized message list.
  const isCompactingRef = useRef(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const olderCursorRef = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Text the composer must take back after a send the server refused. Carries a
  // counter so refusing the same text twice still retriggers the restore.
  const [draftToRestore, setDraftToRestore] = useState<{
    text: string;
    attempt: number;
  } | null>(null);

  // True only while the server is compacting the history for this turn.
  const [isCompactingContext, setIsCompactingContext] = useState(false);

  // True between the click on continue and the first chunk of the continuation.
  //
  // The wait is the same as any other turn's, but what may be shown during it
  // is not: the standard placeholder is a second assistant bubble, and the
  // whole point of a resume is that the answer stays in the one already on
  // screen. Cleared as soon as the request leaves `submitted`, whichever way it
  // goes — the first chunk, an error, or a stop.
  const [isResuming, setIsResuming] = useState(false);

  // The interval the answer being streamed is refreshed at. Mirrored in a ref
  // so the effect below only sets state when a step is actually crossed: a
  // setState per delta would feed the very counter this guards against.
  const [streamThrottleMs, setStreamThrottleMs] = useState(STREAM_THROTTLE_MS);
  const streamThrottleRef = useRef(STREAM_THROTTLE_MS);

  // Set when a turn's response dies on us mid-stream. The turn itself keeps
  // running on the server, so this is a note to re-attach to it rather than an
  // error to show.
  const shouldReattachRef = useRef(false);
  const reattachAttemptsRef = useRef(0);
  const resumeStreamRef = useRef<(() => Promise<void>) | null>(null);
  const refreshThreadRef = useRef<
    (() => Promise<UIMessage<ConversationMessageMetadata>[] | null>) | null
  >(null);
  /**
   * Tells the reader their answer is gone when a refreshed thread still ends on
   * their own question. Assigned each render, because the message it builds
   * reads the user and the model selection.
   *
   * Only called after a re-attach that came back with nothing to stream — the
   * one moment the server has said no turn is running. Once the automatic
   * attempts run out the turn may well be alive, only unreachable, and the
   * database holds no answer until it ends: a card there would say "lost" over
   * an answer still being written.
   */
  const reportUnansweredTurnRef = useRef<
    ((thread: UIMessage<ConversationMessageMetadata>[] | null) => void) | null
  >(null);

  /**
   * Re-attaches to the turn this conversation is running, if any, and then
   * squares the thread with the database. The refresh is what covers the other
   * outcome: a turn that ended while we were away has nothing left to stream,
   * and its answer is only in the database.
   */
  const reattachToRunningTurn = useCallback(async () => {
    shouldReattachRef.current = false;
    // A notice from an earlier attempt would otherwise sit over an answer that
    // is coming back.
    setInlineErrorMessage(null);
    await resumeStreamRef.current?.();
    // A re-attach queued while this one was running means the response dropped
    // again: leave the thread to that attempt instead of overwriting it here.
    if (shouldReattachRef.current) return;
    const refreshedThread = (await refreshThreadRef.current?.()) ?? null;
    reportUnansweredTurnRef.current?.(refreshedThread);
  }, []);

  const { messages, sendMessage, status, stop, setMessages, resumeStream } =
    useChat<UIMessage<ConversationMessageMetadata>>({
      // Keying the chat on the conversation is what lets a turn be picked up
      // again: the reconnect goes out under this id.
      id: conversation.id,
      transport: new DefaultChatTransport({
        api: url(`/conversation/${conversation.id}/message`),
        headers: () => headers(),
        // On the request rather than on the message: the switch is a way of
        // working, and the paths that do not compose a message — continuing a
        // cut answer, above all — carry no metadata to put it on. Resolved per
        // request, so a flip made between two sends is the one that travels.
        body: () => ({
          clarificationEnabled: readClarificationSetting(),
          autoTacticsEnabled: readAutoTacticsSetting(),
          peopleSearchEnabled: isPeopleSearchEnabledRef.current,
        }),
        // A turn is addressed by its conversation, not by a stream id, so the
        // SDK's default `{api}/{chatId}/stream` shape does not apply.
        prepareReconnectToStreamRequest: () => ({
          api: url(`/conversation/${conversation.id}/message/stream`),
        }),
      }),
      // Text deltas arrive token by token: without batching, a long thread
      // re-rendered the whole message list dozens of times per second, which is
      // what made late-conversation streaming stutter. Widened as the answer
      // grows, by the effect below.
      throttle: streamThrottleMs,
      onData: (dataPart) => {
        // The server opens the stream before compacting so it can report that
        // wait; without this the placeholder would sit on "thinking" through a
        // blocking summary call that has nothing to do with thinking.
        if (dataPart.type === "data-compaction") {
          const state = (dataPart.data as { state?: string } | undefined)
            ?.state;
          setIsCompactingContext(state === "running");
        }
        // The work's timeline sends a part at every step: a reader who
        // scrolled up to reread is left where they are.
        if (isPinnedRef.current) scrollToBottom();
      },
      onFinish: ({
        isAbort,
        isDisconnect,
        isError,
        messages: finishedMessages,
      }) => {
        setIsCompactingContext(false);
        if (isAbort) {
          setInlineErrorMessage(null);
          // Marked here rather than only in the stop handler: this also fires
          // when the *server* aborted (deadline, provider error), and it lands
          // after the stop handler's own update, so it must not undo it. Only
          // the stop handler knows the abort was deliberate — the SDK reports
          // every abort the same way — hence the ref.
          setMessagesRef.current?.(
            markLastMessageInterrupted(
              finishedMessages,
              isUserStopRef.current ? "STOPPED_BY_USER" : "INTERRUPTED"
            )
          );
          isUserStopRef.current = false;
          setIsStreamInProgress(false);
          return;
        }

        // The response dropped but the turn did not: the server finishes it
        // whether or not anyone is listening. Re-attaching replays it from its
        // first frame, so the half-written message we have has to go — keeping
        // it would leave the same answer in the thread twice.
        if (isDisconnect) {
          shouldReattachRef.current = true;
          // Nothing is wrong yet — the answer is still coming.
          setInlineErrorMessage(null);
          setMessagesRef.current?.(
            finishedMessages.filter(
              (message, index) =>
                index !== finishedMessages.length - 1 ||
                message.role !== "assistant"
            )
          );
          // Deferred so the SDK can settle this request before the next one
          // starts, and spaced out so a network that is still down does not
          // turn into a reconnect loop.
          if (reattachAttemptsRef.current < MAX_REATTACH_ATTEMPTS) {
            reattachAttemptsRef.current += 1;
            setTimeout(() => {
              if (shouldReattachRef.current) void reattachToRunningTurn();
            }, REATTACH_DELAY_MS);
          } else {
            // Out of automatic attempts. The half-written message was just
            // removed above, so stopping here would leave the composer spinning
            // over a thread missing the very answer it is waiting for. The
            // database is the only source left: it holds the answer if the turn
            // has landed, and the question alone if it has not — either way the
            // thread stops lying. `shouldReattachRef` stays set, so the tab
            // coming back or the network returning still retries with a fresh
            // budget.
            void refreshThreadRef.current?.();
            setIsStreamInProgress(false);
          }
          return;
        }

        // The SDK runs `onFinish` in a `finally`, so a failed turn reaches it
        // right after `onError` — clearing the error here wiped the message the
        // thread was supposed to show, which is why a refused document left the
        // conversation looking as if nothing had happened.
        if (isError) {
          setIsStreamInProgress(false);
          return;
        }

        setInlineErrorMessage(null);
        reattachAttemptsRef.current = 0;
        if (conversation) {
          onMessageGenerated(conversation);
        }
        setIsStreamInProgress(false);
      },
      onError: (error) => {
        setIsCompactingContext(false);
        const lastSubmittedMessage = lastSubmittedMessageRef.current;
        const errorMessage = buildChatErrorMessage({
          error,
          llmId: (lastSubmittedMessage?.llmId ??
            llmSelection ??
            "auto") as LLMModels,
          userId: user?.id ?? null,
          retryable: !!lastSubmittedMessage,
        });
        setInlineErrorMessage(errorMessage);

        // The explanation itself belongs in the thread: it is long, actionable
        // and worth re-reading, which is everything a toast is bad at. The
        // inline error message already carries it, and it is appended to the
        // list in both the optimistic and the loaded branch, so a refusal on a
        // first message shows up too.
        const errorInfo = errorMessage.metadata?.error;
        if (errorInfo?.code && SEND_REFUSED_ERROR_CODES.has(errorInfo.code)) {
          // The composer clears as soon as the message leaves, so a refusal
          // would otherwise cost the user everything they had typed — for a
          // message the server never even stored.
          if (lastSubmittedMessage?.text) {
            setDraftToRestore((previous) => ({
              text: lastSubmittedMessage.text,
              attempt: (previous?.attempt ?? 0) + 1,
            }));
          }
        }
        setIsStreamInProgress(false);
      },
    });
  // Bridged through refs because `useChat` needs these callbacks inside the
  // options it is being built with. Assigned in an effect rather than during
  // render so a render React throws away cannot leave its callbacks behind.
  useEffect(() => {
    setMessagesRef.current = setMessages;
    resumeStreamRef.current = resumeStream;
  });

  // Read through a ref by the handlers the thread is memoized on: `messages`
  // changes on every stream delta, and a callback depending on it would
  // re-render every message of the thread that many times a second.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // D-13: the panel opens itself the instant an Artifact is produced — not
  // every time a conversation that already holds one is reopened, which
  // would pop a panel over a page the user only meant to reread. "Produced"
  // is read literally: only while a round is actually streaming, on the
  // first render where its `tool-showArtifact` or `tool-editArtifact` part
  // settles as a success. A reload (`status` starts at `"ready"`) shows the
  // inline card only, still clickable to reopen the exact same panel (C-02).
  // Keyed by call, not by document: an edit is a new version of the same
  // `artifactId`, and publishing it reloads the panel already showing it.
  const publishedArtifactCallIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (status !== "streaming") return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role !== "assistant") return;

    for (const { toolCallId, artifact } of findProducedArtifacts(
      lastMessage.parts
    )) {
      if (publishedArtifactCallIdsRef.current.has(toolCallId)) continue;
      publishedArtifactCallIdsRef.current.add(toolCallId);
      chatArtifactPanelStore.publish(artifact);
    }
  }, [messages, status]);

  // The store is module-level and outlives this component (D-04's whole
  // point — see `chat-artifact-panel-store.ts`), so leaving a conversation
  // that had a panel open must not leak it into the next one this component
  // remounts for: `ChatLayout` keys `ChatConversation` on `conversation.id`,
  // so switching threads unmounts and remounts this component rather than
  // just changing its props.
  useEffect(() => {
    return () => chatArtifactPanelStore.reset();
  }, []);
  const activeArtifact = useChatArtifactPanel();
  // The sheet slides out over 300ms after the store empties: without this,
  // it would slide out blank.
  const { displayed: mobileSheetArtifact } = useExitTransition(
    activeArtifact,
    ARTIFACT_PANEL_TRANSITION_MS,
  );

  /**
   * Releases the response and keeps whatever the turn had written, without
   * telling the server anything. This is all that sending a new message needs:
   * the incoming turn asks the server to stop the previous one itself, in an
   * order that cannot end with the stop landing on the turn it just started.
   */
  const releaseStreamLocally = useCallback(() => {
    const currentMessages = messages;
    // Released on purpose: neither a re-attach to the turn nor, once `onFinish`
    // reads the ref, an answer marked as one the app failed to finish.
    shouldReattachRef.current = false;
    isUserStopRef.current = true;
    stop();
    if (currentMessages.length > 0) {
      setMessages(
        markLastMessageInterrupted(currentMessages, "STOPPED_BY_USER")
      );
    }
    setInlineErrorMessage(null);
    setIsStreamInProgress(false);
  }, [messages, setIsStreamInProgress, setMessages, stop]);

  /**
   * The Stop button. Closing the response no longer stops a turn — that is what
   * lets one finish in the background — so the server has to be told. Not
   * awaited: the thread settles on the click, not on the round-trip.
   */
  const stopAndKeepPartialMessage = useCallback(() => {
    void api
      .post(`/conversation/${conversation.id}/message/stop`)
      // The thread is released locally either way, so a failure here leaves a
      // turn that still runs behind a UI that says it stopped. Say so, rather
      // than let the two diverge in silence.
      .catch(() =>
        toastError({
          title: t("chat.input.stopFailed.title"),
          description: t("chat.input.stopFailed.description"),
        })
      );
    releaseStreamLocally();
  }, [api, conversation.id, releaseStreamLocally, toastError, t]);

  // Register the stop function with the context, and hand it back on the way
  // out. The provider is mounted above the router, so a registration left
  // behind outlives this conversation: sending a message from a company page
  // would then release the response of the conversation the reader has just
  // left. Only the local release is registered — the server-side stop belongs
  // to the Stop button, while a new message asks the server to end the previous
  // turn itself, in an order that cannot stop the turn it just started.
  useEffect(() => {
    registerStopFunction(releaseStreamLocally);

    return () => registerStopFunction(null);
  }, [releaseStreamLocally, registerStopFunction]);

  // Its own effect, and an empty dependency list: the registration above is
  // rebuilt on every message, so clearing the flag from its cleanup would drop
  // it on every chunk of the answer being streamed. This one only runs when
  // the conversation really goes away.
  useEffect(
    () => () => setIsStreamInProgress(false),
    [setIsStreamInProgress]
  );

  // Update streaming status when status changes
  useEffect(() => {
    const isStreaming = status === "submitted" || status === "streaming";
    setIsStreamInProgress(isStreaming);
  }, [status, setIsStreamInProgress]);

  // The resume flag only describes the wait before the first chunk, so it is
  // dropped the moment the request leaves it — on the chunk, on an error, or on
  // a stop. Cleared here rather than in `onFinish` because a continuation that
  // never starts (the request fails outright) never reaches it, and the flag
  // would then suppress the placeholder of every later turn.
  useEffect(() => {
    if (status !== "submitted") {
      setIsResuming(false);
    }
  }, [status]);

  // Widen the throttle as the answer on screen grows long enough to render
  // slowly, and narrow it again on the next turn. Reading the last message is
  // cheap — it sums the length of a handful of parts, not of the text itself.
  useEffect(() => {
    const next = throttleForAnswerLength(
      answerTextLength(messages[messages.length - 1])
    );
    if (next === streamThrottleRef.current) return;
    streamThrottleRef.current = next;
    setStreamThrottleMs(next);
  }, [messages]);

  const [isAtBottom, setIsAtBottom] = useState(true);
  // Live mirror of "is the view pinned to the bottom" so the scroll-settle loop
  // can read the latest value without stale closures or re-subscriptions.
  const isPinnedRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const settleRafRef = useRef<number | null>(null);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "instant") => {
      const container = scrollContainerRef.current;
      if (container) {
        // Setting scrollTop directly is deterministic, unlike scrollIntoView on
        // a zero-height marker whose alignment can leave us short of the end.
        container.scrollTo({ top: container.scrollHeight, behavior });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior });
      }
    },
    []
  );

  // Keep the thread pinned to the bottom while late-rendering content settles.
  // Some blocks (notably the email card) mount empty and only reach their final
  // height several frames later, so a single scroll lands short of the end. We
  // re-pin every frame until the content height has held steady for a few
  // frames (or we hit the cap), then clear the settling flag so the content is
  // revealed already at the bottom. Bails immediately if the user scrolls up.
  const runScrollSettle = useCallback(() => {
    if (settleRafRef.current !== null) return;
    const container = scrollContainerRef.current;
    const minRevealAt = performance.now() + 80;
    const deadline = performance.now() + 2000;
    let lastHeight = -1;
    let stableFrames = 0;
    const tick = () => {
      const now = performance.now();
      if (!isPinnedRef.current) {
        settleRafRef.current = null;
        setIsSettling(false);
        return;
      }
      scrollToBottom();
      const height = container?.scrollHeight ?? 0;
      if (height === lastHeight) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastHeight = height;
      }
      const settled = stableFrames >= 6 && now >= minRevealAt;
      if (settled || now > deadline) {
        settleRafRef.current = null;
        setIsSettling(false);
        return;
      }
      settleRafRef.current = requestAnimationFrame(tick);
    };
    settleRafRef.current = requestAnimationFrame(tick);
  }, [scrollToBottom]);

  useEffect(
    () => () => {
      if (settleRafRef.current !== null) {
        cancelAnimationFrame(settleRafRef.current);
      }
    },
    []
  );

  // Safety net: never leave the content hidden behind the skeleton if the
  // settle loop somehow never runs (e.g. the composer height never resolves).
  useEffect(() => {
    if (isLoading || !isSettling) return;
    const id = setTimeout(() => setIsSettling(false), 2500);
    return () => clearTimeout(id);
  }, [isLoading, isSettling]);

  // Scroll to the bottom now and follow any content that grows in afterwards.
  const handleAutoScroll = useCallback(() => {
    scrollToBottom();
    runScrollSettle();
  }, [scrollToBottom, runScrollSettle]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const atBottom = scrollHeight - scrollTop - clientHeight < 80;
      // Distinguish a real user scroll-up from content growing in below. Only an
      // actual decrease in scrollTop means the user moved away; content growth
      // (e.g. the email card expanding) leaves scrollTop unchanged while
      // atBottom flips false. Unpinning on that race is what made the settle
      // loop bail mid-growth and stop short of the end.
      const scrolledUp = scrollTop < lastScrollTopRef.current - 4;
      lastScrollTopRef.current = scrollTop;
      if (scrolledUp) {
        isPinnedRef.current = false;
      } else if (atBottom) {
        isPinnedRef.current = true;
      }
      setIsAtBottom(atBottom);
    },
    []
  );

  // Handle quick reply click - send message with the currently selected LLM.
  // `clarificationSkipped` is set only by the clarification card, whose skip
  // controls send a sentence for the model to read and the intent for the turn
  // to act on.
  //
  // Through the ref, like the retry handler: `prepareAndSendMessage` is a bare
  // function rebuilt on every render, so calling it from a `useCallback` that
  // does not depend on it would send whatever the last render to change
  // `llmSelection` had closed over.
  const handleQuickReplyClick = useCallback(
    (reply: string, clarificationSkipped?: ClarificationSkip) => {
      // Released first, like every other send path. The lock and this are two
      // different jobs: the lock refuses a *concurrent* send, this frees the
      // turn that came before. Leaning on the card's own `disabled` instead
      // put the guard in the view, where a card rendered anywhere else does
      // not carry it.
      void stopStreamBeforeSend().then(() => {
        // The card has no draft to hand back — the reply is a click — so a
        // refusal has to say so out loud or the answer simply vanishes.
        const wasTaken = prepareAndSendMessageRef.current({
          llmId: llmSelection ?? "auto",
          text: reply,
          filesAttached: [],
          clarificationSkipped,
        });
        if (!wasTaken) toastError();
      });
    },
    [llmSelection, stopStreamBeforeSend, toastError]
  );

  // Pick up an answer that was cut short: the stop button, the server's
  // wall-clock deadline, or a reply the answer loop could not land inside one
  // turn (`TRUNCATED`) and that the user asks to carry on.
  //
  // `sendMessage()` with no message sends the thread as it stands, ending on
  // the interrupted assistant message: the API answers without persisting a
  // request of its own, and the AI SDK appends the incoming parts to that same
  // message. No "continue" bubble appears, and the answer completes in place.
  //
  // The trailing fragment is dropped first (the API does the same to its own
  // row before reading the context) because the turn died on an arbitrary
  // character: a model resuming from mid-word guesses what it cannot see and
  // the two halves read as broken prose.
  //
  // Memoized like every other handler the thread hands down: `ChatMessage` is
  // a `memo`, and a callback rebuilt on each render defeats it for *every*
  // message on every stream delta — the render cost that made long answers
  // stall in the first place.
  const handleResumeInterruptedMessage = useCallback(() => {
      isUserStopRef.current = false;
      setIsResuming(true);
      // The card explaining why the *previous* attempt failed. Left up, it
      // stays under the answer for the whole of the new attempt and keeps the
      // real tail of the thread one message further down than it is.
      setInlineErrorMessage(null);
      setMessages((current) => {
        const lastIndex = current.length - 1;
        const lastMessage = current[lastIndex];
        if (lastMessage?.role !== "assistant") return current;

        let lastTextIndex = -1;
        lastMessage.parts.forEach((part, index) => {
          if (part.type === "text") lastTextIndex = index;
        });
        if (lastTextIndex === -1) return current;

        const lastTextPart = lastMessage.parts[lastTextIndex];
        if (lastTextPart.type !== "text") return current;
        const truncated = truncateToLastCleanBoundary(lastTextPart.text);
        if (truncated === lastTextPart.text) return current;

        return current.map((message, index) =>
          index === lastIndex
            ? {
                ...message,
                parts: message.parts.map((part, partIndex) =>
                  partIndex === lastTextIndex && part.type === "text"
                    ? { ...part, text: truncated }
                    : part
                ),
              }
            : message
        );
      });
    void sendMessage();
  }, [sendMessage, setMessages]);

  // Edit a previously sent user message: re-submit it with new text. The AI SDK
  // truncates every message after it locally, and the API drops the matching
  // persisted messages, so the conversation continues from this point.
  const handleEditMessage = useCallback(
    (message: UIMessage<ConversationMessageMetadata>, newText: string) => {
      const text = newText.trim();
      if (!text) return;

      void stopStreamBeforeSend().then(() => {
        sendMessage({
          text,
          messageId: message.id,
          metadata: {
            ...message.metadata,
            createdAt: message.metadata?.createdAt ?? new Date(),
            llmId: (llmSelection ?? "auto") as LLMModels,
            userId: message.metadata?.userId ?? user?.id ?? null,
            files: [],
            forcedTool: undefined,
            requestMode: undefined,
            // Rewriting a reply is answering for real, whatever the message it
            // replaces was: a skip must not be inherited.
            clarificationSkipped: undefined,
            prompt: undefined,
          },
        });

        setTimeout(() => {
          scrollToBottom();
        }, 100);
      });
    },
    [
      sendMessage,
      stopStreamBeforeSend,
      llmSelection,
      user?.id,
      scrollToBottom,
    ]
  );

  // Fetch messages on mount, unless this is a freshly created (empty)
  // conversation we'll populate optimistically. Opening a
  // conversation then re-attaches to whatever turn it is still running: turns
  // outlive the view they were started from, so this is how a thread the user
  // left mid-answer comes back streaming rather than frozen on its question.
  useEffect(() => {
    if (initialPendingRef.current) return;
    void fetchMessages(conversation.id).then(() => resumeStreamRef.current?.());
  }, []);

  // A reconnect can only be attempted over a connection that exists. Coming
  // back to the tab, or back online, is the moment to retry one that could not
  // be made — and to hand the automatic attempts a fresh budget, since
  // something outside the page has demonstrably changed.
  useEffect(() => {
    const retry = (): void => {
      if (!shouldReattachRef.current) return;
      if (document.visibilityState === "hidden") return;
      reattachAttemptsRef.current = 0;
      void reattachToRunningTurn();
    };
    document.addEventListener("visibilitychange", retry);
    window.addEventListener("online", retry);
    return () => {
      document.removeEventListener("visibilitychange", retry);
      window.removeEventListener("online", retry);
    };
  }, [reattachToRunningTurn]);

  // Send pending message when available and messages are loaded
  useEffect(() => {
    if (!pendingMessage || isLoading) return;

    prepareAndSendMessage(pendingMessage);

    if (onPendingMessageSent) {
      onPendingMessageSent();
    } else {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [pendingMessage, isLoading]);

  const fetchMessages = async (
    conversationId: string,
    { silent = false }: { silent?: boolean } = {}
  ): Promise<UIMessage<ConversationMessageMetadata>[] | null> => {
    if (!silent) setIsLoading(true);
    try {
      const { data } = await api.get<
        CursorPaginatedResponse<UIMessage<ConversationMessageMetadata>>
      >(`/conversation/${conversationId}/messages?limit=50`);

      const filteredMessages = data.data.filter((m) => m.role !== "system");

      setMessages(filteredMessages);
      olderCursorRef.current = data.nextCursor;
      setHasOlderMessages(data.hasMore);
      // The initial scroll-to-bottom is performed by ChatConversationView in a
      // layout effect, so the thread opens at the latest message before paint
      // instead of flashing at the top and jumping down after load.
      return filteredMessages;
    } catch (error: any) {
      // A silent refresh only ever squares an already-rendered thread with the
      // database, so a failure there is not worth a toast.
      if (!silent) toastError();
      return null;
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // Refreshing behind a reconnect must not put the skeleton back over a thread
  // the user is already reading.
  useEffect(() => {
    refreshThreadRef.current = () =>
      fetchMessages(conversation.id, { silent: true });
  });

  useEffect(() => {
    reportUnansweredTurnRef.current = (thread) => {
      if (!thread || !isLastMessageUnanswered(thread)) return;
      // The thread was fetched before the user sent something new: its last
      // question is the old one, and the card would land on the live turn.
      if (isSendingRef.current) return;

      const lastSubmittedMessage = lastSubmittedMessageRef.current;
      setInlineErrorMessage(
        buildAnswerLostErrorMessage({
          llmId: (lastSubmittedMessage?.llmId ??
            llmSelection ??
            "auto") as LLMModels,
          userId: user?.id ?? null,
        })
      );
    };
  });

  const fetchOlderMessages = useCallback(async () => {
    if (!olderCursorRef.current || isLoadingOlder) return;

    setIsLoadingOlder(true);
    const container = scrollContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const prevScrollTop = container?.scrollTop ?? 0;

    try {
      const { data } = await api.get<
        CursorPaginatedResponse<UIMessage<ConversationMessageMetadata>>
      >(
        `/conversation/${conversation.id}/messages?limit=50&cursor=${encodeURIComponent(olderCursorRef.current)}`
      );

      const olderMessages = data.data.filter((m) => m.role !== "system");

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const unique = olderMessages.filter((m) => !existingIds.has(m.id));
        return [...unique, ...prev];
      });

      olderCursorRef.current = data.nextCursor;
      setHasOlderMessages(data.hasMore);

      // Restore scroll position after prepending
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop =
            newScrollHeight - prevScrollHeight + prevScrollTop;
        }
      });
    } catch (error: any) {
      toastError();
    } finally {
      setIsLoadingOlder(false);
    }
  }, [conversation.id, isLoadingOlder]);

  // Keep a stable handle to the latest fetchOlderMessages so the scroll-to-
  // message loop can call it without listing it as an effect dependency (its
  // identity changes on every isLoadingOlder toggle, which would cancel the loop
  // mid-walk).
  const fetchOlderMessagesRef = useRef(fetchOlderMessages);
  useEffect(() => {
    fetchOlderMessagesRef.current = fetchOlderMessages;
  });

  // Scroll to — and briefly flash — the message referenced by `?m=` (set when a
  // search result is opened). The message may live deep in history, so page
  // older messages in until it is rendered (or history is exhausted), then
  // scroll to it and drop the param so it never re-triggers.
  useEffect(() => {
    if (isLoading) return;
    if (!targetMessageId || handledTargetRef.current === targetMessageId) return;
    handledTargetRef.current = targetMessageId;

    let cancelled = false;
    const nextFrame = () =>
      new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    const findEl = () =>
      scrollContainerRef.current?.querySelector<HTMLElement>(
        `[data-message-id="${CSS.escape(targetMessageId)}"]`
      ) ?? null;

    void (async () => {
      let el = findEl();
      let guard = 0;
      // Cap the walk-back so a stale/deleted id can never loop forever.
      while (!el && olderCursorRef.current && !cancelled && guard < 50) {
        await fetchOlderMessagesRef.current();
        await nextFrame();
        el = findEl();
        guard += 1;
      }
      if (cancelled) return;

      if (el) {
        el.scrollIntoView({ block: "center" });
        setHighlightedMessageId(targetMessageId);
        setTimeout(() => {
          if (!cancelled) setHighlightedMessageId(null);
        }, 2600);
      }

      // Remove the param so a refresh or later re-render never re-jumps.
      setSearchParams(
        (prev) => {
          prev.delete("m");
          return prev;
        },
        { replace: true }
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, targetMessageId, setSearchParams]);

  /**
   * Uploads whatever is attached and hands the message to the SDK. Holds the
   * send lock for its whole length — see `prepareAndSendMessage`, the only
   * caller, which takes the lock before starting it.
   */
  const runSend = async (newMessage: MemoryMessageToSend): Promise<void> => {
    setInlineErrorMessage(null);
    isUserStopRef.current = false;

    // The aim a reply to a question inherits from the request it answers.
    // Resolved here rather than in the card's handler so the composer carries
    // it too: answering a question by typing rather than by clicking used to
    // drop the tick silently for the rest of the run.
    const { forcedTool, requestMode } = resolveRequestAim(
      messagesRef.current,
      newMessage
    );

    // The upload runs before the message is sent, so a failure here means
    // neither the file nor the message ever leaves the browser. Without this
    // guard the rejection was unhandled: the composer simply cleared and the
    // user was left thinking the document had been sent.
    let newFilesDto: ConversationFileDTO[];
    try {
      newFilesDto = newMessage.uploadedFiles
        ? newMessage.uploadedFiles
        : newMessage.filesAttached.length
        ? await uploadFiles(newMessage.filesAttached, conversation.id)
        : [];
    } catch (error) {
      // The composer clears as soon as the message leaves it, so an upload that
      // fails used to cost the user everything they had typed — which is why
      // each of these descriptions ended on "copy your message before trying
      // again". Handing the draft back is what lets them say the useful half
      // only: what went wrong.
      if (newMessage.text) {
        setDraftToRestore((previous) => ({
          text: newMessage.text,
          attempt: (previous?.attempt ?? 0) + 1,
        }));
      }
      // Name the actual cause: "retry in a moment" and "send a smaller file"
      // are opposite advice, and giving the wrong one sends the user chasing a
      // problem that isn't theirs.
      const reason = resolveUploadFailureReason(error);
      toastError({
        title: t(`chat.input.files.uploadFailed.${reason}.title`),
        description: t(`chat.input.files.uploadFailed.${reason}.description`, {
          size: FILES_UPLOAD_SIZE_LIMIT_LABEL,
        }),
      });
      setHasSendFailed(true);
      return;
    }

    // Nothing is asked here about a document already sent: the composer answered
    // that while the file was still removable. Interrupting a written message
    // with the question was the wrong moment for it — the only choices a modal
    // can offer at that point are "send anyway" or "lose your place".
    lastSubmittedMessageRef.current = {
      ...newMessage,
      forcedTool,
      requestMode,
      uploadedFiles: newFilesDto,
    };

    sendMessage({
      role: "user",
      parts: [
        {
          type: "text",
          text: newMessage.text,
        },
        ...newFilesDto.map(
          (file) =>
            ({
              type: "file" as const,
              mediaType: file.mediaType,
              filename: file.name,
              url: file.url,
            } as FileUIPart)
        ),
      ],
      metadata: {
        createdAt: new Date(),
        llmId: newMessage.llmId as LLMModels,
        userId: user?.id || null,
        files: newFilesDto,
        forcedTool: forcedTool ?? undefined,
        requestMode: requestMode ?? undefined,
        clarificationSkipped: newMessage.clarificationSkipped,
        companyContext: newMessage.companyContext
          ? {
              id: newMessage.companyContext.id,
              name: newMessage.companyContext.name,
              domain: newMessage.companyContext.domain,
              logo: newMessage.companyContext.logo,
              thumbnails: newMessage.companyContext.thumbnails,
            }
          : undefined,
        prompt: newMessage.prompt,
      },
    });
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  /**
   * Takes the send lock and starts the send, or refuses because one is already
   * under way.
   *
   * One send at a time. A lock rather than a check on `status`, for two
   * reasons: `status` only leaves "ready" once `sendMessage` has been reached,
   * so the upload and the click-to-click gap on a button whose effect is not
   * visible yet are both windows where a second call opens a second turn — and
   * sending *while* a turn runs is a supported flow, one the callers open with
   * `stopStreamBeforeSend`, so refusing on `status` would swallow the message
   * the user meant to interrupt it with.
   *
   * Answers synchronously, and before the upload: the composer clears as soon
   * as a send is taken, so it has to learn about a refusal in the same tick or
   * it clears over a message that never left.
   *
   * @returns `false` when nothing was sent and nothing will be — the caller
   * keeps what it holds.
   */
  const prepareAndSendMessage = (newMessage: MemoryMessageToSend): boolean => {
    if (isSendingRef.current) return false;
    isSendingRef.current = true;
    // Released whichever way the send ends, including a throw: a lock left
    // held would make every later send a silent no-op. The failure is caught
    // rather than left to float — the draft is already gone from the composer,
    // so the toast is the only thing telling the user their message did not
    // leave.
    void runSend(newMessage)
      .catch((error: unknown) => {
        if (newMessage.text) {
          setDraftToRestore((previous) => ({
            text: newMessage.text,
            attempt: (previous?.attempt ?? 0) + 1,
          }));
        }
        toastError();
        setHasSendFailed(true);
        console.error("Failed to send the message", error);
      })
      .finally(() => {
        isSendingRef.current = false;
      });
    return true;
  };

  const uploadFiles = async (files: File[], conversationId: string) => {
    let newFiles: ConversationFileDTO[] = [];

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const { data } = await api.post<{
      success: boolean;
      files: ConversationFileDTO[];
    }>(`/conversation/${conversationId}/files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      maxContentLength: 100 * 1024 * 1024, // 100mb
      maxBodyLength: 100 * 1024 * 1024, // 100mb
    });

    newFiles = data.files;
    return newFiles;
  };

  // `prepareAndSendMessage` closes over the whole send pipeline and is rebuilt
  // on every render, so the retry handler reads it through a ref: that keeps the
  // handler identity stable and lets the memoized message list skip re-rendering
  // on each stream delta.
  const prepareAndSendMessageRef = useRef(prepareAndSendMessage);
  prepareAndSendMessageRef.current = prepareAndSendMessage;

  /**
   * Compacts the conversation on demand: the server summarizes the history
   * behind a compaction boundary and returns the resulting thread. The only way
   * out when the context is full — a retry would rebuild the same oversized
   * history and fail again.
   */
  const handleCompactConversation = useCallback(async () => {
    if (isCompactingRef.current) return;

    isCompactingRef.current = true;
    setIsCompacting(true);
    try {
      const { data } = await api.post<
        UIMessage<ConversationMessageMetadata>[]
      >(`/conversation/${conversation.id}/compact`);

      setMessages(data.filter((m) => m.role !== "system"));
      setInlineErrorMessage(null);
      scrollToBottom();
    } catch {
      toastError();
    } finally {
      isCompactingRef.current = false;
      setIsCompacting(false);
    }
  }, [api, conversation.id, scrollToBottom, setMessages, toastError]);

  const handleRetryLastMessage = useCallback(() => {
    const lastSubmittedMessage = lastSubmittedMessageRef.current;
    if (!lastSubmittedMessage) return;

    void stopStreamBeforeSend().then(() => {
      void prepareAndSendMessageRef.current(lastSubmittedMessage);
    });
  }, [stopStreamBeforeSend]);

  // Until the live stream populates the first message, show the pending message
  // optimistically as a user bubble plus the "thinking" placeholder.
  const optimisticMessage = useMemo(
    () =>
      initialPendingRef.current
        ? buildOptimisticUserMessage(initialPendingRef.current, user?.id ?? null)
        : null,
    [user?.id]
  );
  const showOptimistic = messages.length === 0 && optimisticMessage !== null;
  // The optimistic branch used to replace the whole list, which dropped the
  // error message: a first message refused before it was ever stored showed the
  // user bubble, then nothing at all. The error is appended in both cases, and
  // the forced "submitted" status is lifted once there is one, or once the send
  // failed before it left — otherwise the thinking placeholder would spin, and
  // the composer stay in stop, forever over a request that already failed.
  const baseMessages = showOptimistic ? [optimisticMessage] : messages;
  const displayMessages = inlineErrorMessage
    ? [...baseMessages, inlineErrorMessage]
    : baseMessages;
  const displayStatus = resolveOptimisticStatus({
    status,
    isShowingOptimistic: showOptimistic,
    hasInlineError: inlineErrorMessage !== null,
    hasSendFailed,
  });
  const hasContent =
    messages.length > 0 || showOptimistic || inlineErrorMessage !== null;

  // Unchanged from before this ticket, on purpose (C-06): a conversation with
  // no Artifact open renders exactly this tree. On desktop it sits in the
  // split's first panel at full width, the Artifact panel collapsed to zero.
  const conversationContent = (
    <div
      className={cn(
        "absolute flex flex-col w-full h-full bg-muted/50",
        isMobile && "pb-(--mobile-bottom-inset)"
      )}
    >
      {isLoading && <ChatConversationSkeleton />}
      {!isLoading && !hasContent && (
        <ChatNew
          onMessageSent={prepareAndSendMessage}
          streamInProgress={status === "streaming"}
          companyTargeted={conversation.companyTargeted}
        />
      )}
      {!isLoading && hasContent && (
        <div
          className={cn(
            "flex relative flex-col flex-1 min-h-0",
            isSettling && "invisible"
          )}
        >
          <ChatConversationView
            conversationId={conversation.id}
            messages={displayMessages}
            status={displayStatus}
            onMessageSent={prepareAndSendMessage}
            // `displayStatus`, not `status`: the optimistic first message is a
            // turn in progress, and the composer taking over from the creation
            // placeholder must not flash back to send before the stream opens.
            streamInProgress={
              displayStatus === "submitted" || displayStatus === "streaming"
            }
            triggerStopStream={stopAndKeepPartialMessage}
            onQuickReplyClick={handleQuickReplyClick}
            onEditMessage={handleEditMessage}
            onRetryMessage={handleRetryLastMessage}
            isCompactingContext={isCompactingContext}
            isResuming={isResuming}
            draftToRestore={draftToRestore}
            onCompactConversation={handleCompactConversation}
            isCompacting={isCompacting}
            onResumeMessage={handleResumeInterruptedMessage}
            hasOlderMessages={hasOlderMessages}
            isLoadingOlder={isLoadingOlder}
            onLoadOlder={fetchOlderMessages}
            scrollContainerRef={scrollContainerRef}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
            showScrollToBottom={!isAtBottom}
            onScrollToBottom={() => scrollToBottom("smooth")}
            onAutoScroll={handleAutoScroll}
            disableInitialAutoScroll={hasInitialScrollTargetRef.current}
            highlightedMessageId={highlightedMessageId}
            initialCompanyContext={conversation.companyTargeted}
            // Off for a conversation about a company, which already knows
            // which one it means, and while a question awaits its answer:
            // whatever is typed then is that answer, even a bare SIRET.
            isCompanyIdentifierShortcutEnabled={
              !conversation.companyTargeted &&
              !asksClarification(messages[messages.length - 1])
            }
            // The creation placeholder already showed stop, so the composer
            // taking over from it starts there; any other one mounting
            // mid-stream takes over from the welcome screen's send.
            composerStartsFromSend={!initialPendingRef.current}
          />
        </div>
      )}
      {!isLoading && hasContent && isSettling && (
        <ChatConversationSkeleton className="absolute inset-0" />
      )}
    </div>
  );

  // Mobile: the panel is a full-screen sheet over the conversation (D-04's
  // desktop split has nowhere to put a second column on a phone screen), so
  // the conversation tree itself never changes shape — only whether the
  // sheet is mounted on top of it changes (C-09).
  if (isMobile) {
    return (
      <>
        {conversationContent}
        <MobileSheet
          // The panel's header already has its own close action.
          hideCloseButton
          open={!!activeArtifact}
          onOpenChange={(open) => {
            if (!open) chatArtifactPanelStore.close();
          }}
        >
          <ChatArtifactPanel
            artifact={mobileSheetArtifact}
            conversationId={conversation.id}
          />
        </MobileSheet>
      </>
    );
  }

  // Desktop: always the split, the Artifact panel collapsed to zero when
  // nothing is open, so opening one never remounts the conversation.
  return (
    <ChatArtifactSplit conversationId={conversation.id}>
      {conversationContent}
    </ChatArtifactSplit>
  );
}

/**
 * Presentational shell shared by the live conversation and the optimistic
 * "creating" state: a scrollable message list plus the input footer. Keeping a
 * single shell guarantees the optimistic view and the real one are pixel
 * identical, so nothing shifts when the live stream takes over.
 */
export function ChatConversationView({
  conversationId = "",
  messages,
  status,
  onMessageSent,
  streamInProgress,
  triggerStopStream,
  onQuickReplyClick,
  onEditMessage,
  onRetryMessage,
  isCompactingContext = false,
  isResuming = false,
  draftToRestore,
  onCompactConversation,
  isCompacting = false,
  onResumeMessage,
  hasOlderMessages = false,
  isLoadingOlder = false,
  onLoadOlder,
  scrollContainerRef,
  messagesEndRef,
  onScroll,
  showScrollToBottom = false,
  onScrollToBottom,
  onAutoScroll,
  disableInitialAutoScroll = false,
  highlightedMessageId = null,
  initialCompanyContext = null,
  isCompanyIdentifierShortcutEnabled = false,
  composerStartsFromSend = false,
}: {
  conversationId?: string;
  messages: UIMessage<ConversationMessageMetadata>[];
  status: ChatStatus;
  /** See `ChatTextInput`'s own prop: `false` means the send was refused. */
  onMessageSent: (params: MemoryMessageToSend) => boolean | void;
  streamInProgress: boolean;
  triggerStopStream: () => void;
  initialCompanyContext?: CompanyDTO | null;
  onQuickReplyClick?: (reply: string, skipped?: ClarificationSkip) => void;
  onEditMessage?: (
    message: UIMessage<ConversationMessageMetadata>,
    newText: string
  ) => void;
  onRetryMessage?: () => void;
  isCompactingContext?: boolean;
  /** Whether this turn is a continuation of the bubble already on screen. */
  isResuming?: boolean;
  draftToRestore?: { text: string; attempt: number } | null;
  onCompactConversation?: () => void;
  isCompacting?: boolean;
  /** Asks the assistant to pick up an interrupted answer where it stopped. */
  onResumeMessage?: () => void;
  hasOlderMessages?: boolean;
  isLoadingOlder?: boolean;
  onLoadOlder?: () => void;
  scrollContainerRef?: Ref<HTMLDivElement>;
  messagesEndRef?: Ref<HTMLDivElement>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  showScrollToBottom?: boolean;
  onScrollToBottom?: () => void;
  onAutoScroll?: () => void;
  /** Skip the open-at-bottom jump (a `?m=` scroll-to-message will take over). */
  disableInitialAutoScroll?: boolean;
  highlightedMessageId?: string | null;
  /** See `ChatTextInput`'s own prop: off for a conversation about a company. */
  isCompanyIdentifierShortcutEnabled?: boolean;
  /** See `ChatTextInput`'s own prop. */
  composerStartsFromSend?: boolean;
}) {
  const { t } = useTranslation();

  // The window of the turn the user is looking at: the newest assistant message
  // that carries one. A turn cut before the model answered has no usage of its
  // own, so falling back to the one before it keeps the ring from blanking out.
  const contextUsageInfo = useMemo<ChatContextUsageInfo | undefined>(() => {
    if (!conversationId) return undefined;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role !== "assistant") continue;
      const usage = message.metadata?.contextUsage;
      if (usage) {
        return {
          usage,
          conversationId,
          messages,
          llmId: message.metadata?.llmId,
        };
      }
    }
    return undefined;
  }, [conversationId, messages]);

  // The composer floats over the scroll area so messages scroll up behind its
  // rounded corners instead of being cut off by an opaque footer. Its height is
  // mirrored as bottom padding on the message list so the last message can
  // still rest fully above the input.
  const composerRef = useRef<HTMLDivElement>(null);
  const [composerHeight, setComposerHeight] = useState(0);
  // Keeps the composer above the on-screen keyboard as the transcript grows.
  const keyboardInset = useKeyboardInset();
  const { isMobile } = useDeviceFormat();
  // On a phone the composer already sits above the home indicator's safe
  // area, which the keyboard covers first: only the part of the keyboard taller
  // than that inset lifts it.
  const composerBottom =
    isMobile && keyboardInset > 0
      ? `max(0px, calc(${keyboardInset}px - var(--mobile-bottom-inset)))`
      : keyboardInset;

  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    const update = () => setComposerHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The composer floats (absolute) over the scroll area, so it sits outside the
  // scrollbar-gutter the scroll container reserves on each edge. Rather than
  // guess that gutter's width, we mirror the message column's actual box so the
  // input always lines up with the message bubbles, whatever the scrollbar does.
  const rootRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const [composerBox, setComposerBox] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const column = columnRef.current;
    if (!root || !column) return;
    const update = () => {
      const rootRect = root.getBoundingClientRect();
      const columnRect = column.getBoundingClientRect();
      setComposerBox((prev) => {
        const next = {
          left: columnRect.left - rootRect.left,
          width: columnRect.width,
        };
        if (prev && prev.left === next.left && prev.width === next.width) {
          return prev;
        }
        return next;
      });
    };
    update();
    // Re-measure after the first paint as well: the scrollbar gutter can resolve
    // a frame late, which would otherwise leave the composer on its full-width
    // fallback and overhang the message column.
    const raf = requestAnimationFrame(update);
    // Observe the column directly so any width change (gutter, breakpoint,
    // panel resize) keeps the composer locked to the message column.
    const observer = new ResizeObserver(update);
    observer.observe(column);
    observer.observe(root);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  // Jump to the latest message before the browser paints. We wait for the
  // composer height so its bottom padding is already reserved when we scroll,
  // and doing it in a layout effect (after the synchronous re-render the height
  // measurement triggers) means the list is positioned before paint — the view
  // never flashes at the top and then jumps down. onAutoScroll also keeps the
  // view pinned for a short window while late content (e.g. the email card)
  // grows in, so we end on the last message instead of short of it.
  const didInitialScrollRef = useRef(false);
  useLayoutEffect(() => {
    if (didInitialScrollRef.current || composerHeight === 0) return;
    didInitialScrollRef.current = true;
    // When opening on a specific message (search jump), skip the bottom jump so
    // the scroll-to-message logic can position the view instead.
    if (disableInitialAutoScroll) return;
    onAutoScroll?.();
  }, [composerHeight, onAutoScroll, disableInitialAutoScroll]);

  return (
    <div ref={rootRef} className="flex relative flex-col flex-1 min-h-0">
      <div
        ref={scrollContainerRef}
        onScroll={onScroll}
        className="overflow-auto overscroll-y-contain flex-1 [scrollbar-gutter:stable_both-edges]"
        // The composer floats over the bottom of this scroll area, so reserve
        // its height as scroll padding: scrollIntoView (company-mention chevrons)
        // then lands a chip just above the input rather than behind it.
        style={{ scrollPaddingBottom: composerHeight }}
      >
        <div className="flex flex-col size-full">
          <div
            ref={columnRef}
            className="w-full max-w-3xl mx-auto"
            style={{ paddingBottom: composerHeight }}
          >
            <ChatMessages
              messages={messages}
              status={status}
              onQuickReplyClick={onQuickReplyClick}
              onEditMessage={onEditMessage}
              onRetryMessage={onRetryMessage}
              isCompactingContext={isCompactingContext}
              isResuming={isResuming}
              onCompactConversation={onCompactConversation}
              isCompacting={isCompacting}
              onResumeMessage={onResumeMessage}
              hasOlderMessages={hasOlderMessages}
              isLoadingOlder={isLoadingOlder}
              onLoadOlder={onLoadOlder}
              highlightedMessageId={highlightedMessageId}
            />
          </div>
          <div ref={messagesEndRef} />
        </div>
      </div>

      <Button
        type="button"
        size="icon"
        variant="outline"
        onClick={onScrollToBottom}
        aria-label={t("chat.conversation.scrollToBottom")}
        aria-hidden={!showScrollToBottom}
        tabIndex={showScrollToBottom ? 0 : -1}
        style={{ bottom: composerHeight + 8 }}
        className={cn(
          "absolute left-1/2 z-20 rounded-full shadow-md -translate-x-1/2 bg-background hover:bg-muted",
          "transition-all duration-200 ease-out",
          showScrollToBottom
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-3 pointer-events-none"
        )}
      >
        <ArrowDownIcon className="size-4" />
      </Button>

      <div
        ref={composerRef}
        className={cn(
          "absolute bottom-0 z-10 px-3 transition-[bottom] duration-150",
          // Floating: a gap under it rather than the disclaimer tray.
          isMobile && "pb-2"
        )}
        style={{
          ...(composerBox
            ? { left: composerBox.left, width: composerBox.width }
            : { left: 0, right: 0 }),
          // Lift the composer clear of the on-screen keyboard. Zero wherever
          // the browser already shrinks the layout viewport for it.
          bottom: composerBottom,
        }}
      >
        <div className="relative">
          <div className="relative z-10">
            <ChatTextInput
              onMessageSent={onMessageSent}
              conversationId={conversationId}
              draftToRestore={draftToRestore}
              streamInProgress={streamInProgress}
              triggerStopStream={triggerStopStream}
              initialCompanyContext={initialCompanyContext}
              contextUsage={contextUsageInfo}
              isCompanyIdentifierShortcutEnabled={
                isCompanyIdentifierShortcutEnabled
              }
              startsFromSend={composerStartsFromSend}
            />
          </div>
          {/* The tray docks the composer to the bottom edge; a phone's
              composer floats instead. */}
          {!isMobile && <ChatComposerDisclaimer />}
        </div>
      </div>
    </div>
  );
}
